import {useCallback, useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  FiltersGroupId,
  FilterIds,
  PlatformFilterIds,
  CrmFilterType,
} from '@/types/models/creator-crm/filterId';
import {LdaUpdate} from '@/types/models/creator-crm/filterUpdate';
import {
  ActiveFilterMetadata,
  getActiveFiltersMetadataForFilter,
} from '@/stores/creator-crm/activeFilterExtractors';
import useCreatorCrmStore, {
  creatorCrmActions,
} from '@/stores/creator-crm/creatorCrmStore';

export type ActiveFilterWithRemovalContext = {
  filterMetadata: ActiveFilterMetadata;
  groupId: FiltersGroupId;
  filterId: FilterIds;
  platformFilterId?: PlatformFilterIds;
};

export type GroupedActiveFilter = {
  groupKey: string;
  filters: ActiveFilterWithRemovalContext[];
};

/**
 * True when the group represents the platforms/platform filter only (used when handle search shows only platform chips in the bar).
 */
export function isPlatformGroup(group: GroupedActiveFilter): boolean {
  return group.groupKey.startsWith('platforms-platform-');
}

export function getPlatformGroups(
  groups: GroupedActiveFilter[]
): GroupedActiveFilter[] {
  return groups.filter(isPlatformGroup);
}

type ActiveFiltersBarState = {
  groupedActiveFilters: GroupedActiveFilter[];
  hasActiveFilters: boolean;
  handleRemoveFilter: (activeFilter: ActiveFilterWithRemovalContext) => void;
};

export default function useActiveFiltersBar(): ActiveFiltersBarState {
  const filtersGroupsSnapshot = useCreatorCrmStore(
    useShallow((state) => state.content.filtersState.filtersGroupsSnapshot)
  );

  const groupedActiveFilters = useMemo(() => {
    const activeFilters = Object.entries(filtersGroupsSnapshot).flatMap(
      ([groupId, group]) =>
        Object.entries(group.filters).flatMap(([filterId, filter]) => {
          const metadatas = getActiveFiltersMetadataForFilter(filter);
          return metadatas.map(
            (filterMetadata): ActiveFilterWithRemovalContext => ({
              filterMetadata,
              groupId: groupId as FiltersGroupId,
              filterId: filterId as FilterIds,
              platformFilterId: filterMetadata.platformFilterId,
            })
          );
        })
    );

    const groupsByKey = new Map<string, ActiveFilterWithRemovalContext[]>();
    activeFilters.forEach((af) => {
      const key = getFilterGroupKey(af);
      const existing = groupsByKey.get(key);
      if (existing) {
        existing.push(af);
      } else {
        groupsByKey.set(key, [af]);
      }
    });

    return Array.from(groupsByKey.entries(), ([groupKey, filters]) => ({
      groupKey,
      filters,
    }));
  }, [filtersGroupsSnapshot]);

  const handleRemoveFilter = useCallback(
    (activeFilter: ActiveFilterWithRemovalContext) => {
      applyActiveFilterRemoval(activeFilter);
      creatorCrmActions.applyFiltersGroup({
        closeDrawer: false,
        requireDrawerOpen: false,
      });
    },
    []
  );

  return {
    groupedActiveFilters,
    hasActiveFilters: groupedActiveFilters.length > 0,
    handleRemoveFilter,
  };
}

function getFilterGroupKey(
  activeFilter: ActiveFilterWithRemovalContext
): string {
  const platformKey = String(activeFilter.platformFilterId ?? 'all');
  return `${activeFilter.groupId}-${activeFilter.filterId}-${platformKey}`;
}

/**
 * Unique key for an active filter chip. Uses option.id when available (e.g. locations)
 * so duplicate display values (e.g. two "New Jersey" entries) get distinct keys.
 */
export function getActiveFilterKey(
  activeFilter: ActiveFilterWithRemovalContext
): string {
  const meta = activeFilter.filterMetadata;
  const uniqueValue =
    'option' in meta && meta.option != null && meta.option.id != null
      ? meta.option.id
      : meta.value;
  return `${getFilterGroupKey(activeFilter)}-${uniqueValue}`;
}

function applyActiveFilterRemoval(
  activeFilter: ActiveFilterWithRemovalContext
): void {
  const handler = removalHandlers[
    activeFilter.filterMetadata.filterType
  ] as AnyRemovalHandler;
  handler(activeFilter);
}

type AnyRemovalHandler = (activeFilter: ActiveFilterWithRemovalContext) => void;

// eslint-disable-next-line @typescript-eslint/naming-convention -- ActiveFilterByType is the requested name
type ActiveFilterByType<K extends CrmFilterType> =
  ActiveFilterWithRemovalContext & {
    filterMetadata: Extract<ActiveFilterMetadata, {filterType: K}>;
  };

type RemovalHandler<K extends CrmFilterType> = (
  activeFilter: ActiveFilterByType<K>
) => void;

type RemovalHandlerMap = {
  [K in CrmFilterType]: RemovalHandler<K>;
};

const forceChangeOptionSelection = (
  activeFilter:
    | ActiveFilterByType<'singleSelection'>
    | ActiveFilterByType<'weightedSingleSelection'>
    | ActiveFilterByType<'multiSelection'>
    | ActiveFilterByType<'multiSelectionAsync'>
    | ActiveFilterByType<'weightedMultiSelection'>
) => {
  creatorCrmActions.forceChangeFilterOptionSelection(
    activeFilter.filterMetadata.filterType,
    activeFilter.groupId,
    activeFilter.filterId,
    activeFilter.filterMetadata.option,
    activeFilter.platformFilterId
  );
};

const removalHandlers: RemovalHandlerMap = {
  singleSelection: forceChangeOptionSelection,
  weightedSingleSelection: forceChangeOptionSelection,
  multiSelection: forceChangeOptionSelection,
  multiSelectionAsync: forceChangeOptionSelection,
  weightedMultiSelection: forceChangeOptionSelection,
  multiUserInput: (activeFilter) => {
    creatorCrmActions.changeUserInputFilter(
      activeFilter.groupId,
      activeFilter.filterId,
      activeFilter.filterMetadata.input,
      activeFilter.platformFilterId,
      false
    );
  },
  singleUserInput: (activeFilter) => {
    creatorCrmActions.changeSingleUserInputFilter(
      activeFilter.groupId,
      activeFilter.filterId,
      '',
      activeFilter.platformFilterId,
      false
    );
  },
  rangeSelection: (activeFilter) => {
    creatorCrmActions.forceChangeFilterRange(
      activeFilter.groupId,
      activeFilter.filterId,
      undefined,
      activeFilter.platformFilterId
    );
  },
  audienceAge: (activeFilter) => {
    if (activeFilter.filterMetadata.kind === 'lda') {
      const ldaUpdate: LdaUpdate = {
        updateType: 'lda',
        isLdaSelected: false,
      };
      creatorCrmActions.updateFilterOption(
        'audienceAge',
        activeFilter.groupId,
        activeFilter.filterId,
        ldaUpdate,
        false
      );
      return;
    }

    creatorCrmActions.forceChangeFilterOptionSelection(
      'audienceAge',
      activeFilter.groupId,
      activeFilter.filterId,
      activeFilter.filterMetadata.option,
      activeFilter.platformFilterId
    );
  },
  dynamicPlatformFilters() {
    throw new Error(
      '`dynamicPlatformFilters` should never be removed directly; it expands into nested platform filters.'
    );
  },
};
